import domtoimage from 'dom-to-image';
import { IPoint } from 'utils/GeometryUtils';

export function outerHeight(el: HTMLElement) {
  var height = el.offsetHeight;
  var style = getComputedStyle(el);

  height += parseInt(style.marginTop || '') + parseInt(style.marginBottom || '');
  return height;
}

export function outerWidth(el: HTMLElement) {
  var width = el.offsetWidth;
  var style = getComputedStyle(el);

  width += parseInt(style.marginLeft || '') + parseInt(style.marginRight || '');
  return width;
}

export function createEvent(eventName: string, data: any = null): Event | CustomEvent {
  if (!data) {
    let event: Event;

    if (typeof Event === 'function') {
      event = new Event(eventName);
    } else {
      event = document.createEvent('Event');
      event.initEvent(eventName, true, true);
    }

    return event;
  } else {
    let event: CustomEvent;

    if (typeof CustomEvent === 'function') {
      event = new CustomEvent(eventName, { detail: data });
    } else {
      event = document.createEvent('CustomEvent');
      event.initCustomEvent(eventName, true, true, data);
    }

    return event;
  }
}

export function isDetached(el: HTMLElement) {
  return !el.closest('html');
}

export const DEFAULT_ZINDEX_VALUE = 999;
/**
 * Finds all elements with "ui-front" className and returns the maximum zIndex.
 * For components you might need to use @method useMaxZIndex() hook
 *
 * @returns max z-index
 */
export function getMaxZIndex(): number {
  const layers = document.getElementsByClassName('ui-front');
  const zIndexes: number[] = [];
  for (let item of Array.from(layers)) {
    if (document.defaultView != null) {
      const zIndex = parseFloat(document.defaultView.getComputedStyle(item, null).getPropertyValue('z-index'));
      if (!isNaN(zIndex)) {
        zIndexes.push(zIndex);
      }
    }
  }
  let maxZIndex = DEFAULT_ZINDEX_VALUE;
  if (zIndexes.length) {
    maxZIndex = Math.max(...zIndexes) + 1;
  }

  if (IS_TEST) {
    // keep this constant for Jest tests, so snapshots aren`t constantly changing.
    // Webpack will remove this "dead-code" for non-test builds.
    maxZIndex = 1000;
  }

  return maxZIndex;
}

export function isElementVisible(element: HTMLElement) {
  if (!element) return false;
  const visible = !!(element.offsetWidth || element.offsetHeight || element.getClientRects().length);
  if (!visible) return false;
  const style = getComputedStyle(element);
  if (style.visibility !== 'visible') return false;
  if ((style.opacity as any) === 0) return false;
  return true;
}

export function getOffset(element: HTMLElement) {
  if (!element) return { top: 0, left: 0 };
  const rect = element.getBoundingClientRect(),
    scrollLeft = window.pageXOffset || document.documentElement.scrollLeft,
    scrollTop = window.pageYOffset || document.documentElement.scrollTop;
  return { top: rect.top + scrollTop, left: rect.left + scrollLeft };
}

export function getClosest(node: HTMLElement, className: string): HTMLElement | null {
  if (node.classList.contains(className)) {
    return node;
  }
  if (node.parentElement && node.parentElement.nodeName !== 'BODY') {
    return getClosest(node.parentElement as HTMLElement, className);
  }
  return null;
}

export function isInViewport(element: HTMLElement) {
  const rect = element.getBoundingClientRect();
  return (
    rect.top >= 0 &&
    rect.left >= 0 &&
    rect.bottom <= (window.innerHeight || document.documentElement.clientHeight) &&
    rect.right <= (window.innerWidth || document.documentElement.clientWidth)
  );
}

export function isInViewportWithOverflowX(element: HTMLElement) {
  const rect = element.getBoundingClientRect();
  return (
    rect.top >= 0 && rect.left >= 0 && rect.bottom <= (window.innerHeight || document.documentElement.clientHeight)
  );
}

export function copyToClipboard(text: string) {
  if (!text) return;
  const elem = document.createElement('textarea');
  document.body.appendChild(elem).select();
  elem.value = text;
  elem.select();
  document.execCommand('copy');
  document.body.removeChild(elem);
}

export const DOMHelpers = {
  getMaxZIndex,
  generatePreviewURL,
  domToImageDefaultFilters,
  outerHeight,
  outerWidth,
};

export function getInnerWidth(node: HTMLElement | null) {
  if (!node) return null;
  var style = getComputedStyle(node);
  return node.clientWidth - (parseInt(style.paddingRight || '') + parseInt(style.paddingLeft || ''));
}

/**
 * Force embedded `<script>` tags to be executed. This is necessary since we are not using jQuery in this project,
 * but a lot of MVC stuff still depends on `<script>` evaluation from HTML partials, and `innerHTML` etc. do not
 * evaluate scripts like `jQuery.append(stringData)` does;
 *
 * NOTE: This didn't behave / load things quite the same way as $.append(), so it introduced some race conditions
 * where a bundle might not be evaluated when something depending on it was asking for it. Currently not being used,
 * might need some attention before it's ready for production.
 *
 * @param containerElement with embedded script tags (e.g. children set by `element.innerHTML = someString`)
 */
export function executeScriptElements(containerElement: HTMLElement) {
  const scriptElements = containerElement.querySelectorAll('script');

  Array.from(scriptElements).forEach(scriptElement => {
    const clonedElement = document.createElement('script');

    Array.from(scriptElement.attributes).forEach(attribute => {
      clonedElement.setAttribute(attribute.name, attribute.value);
    });

    clonedElement.text = scriptElement.text;

    scriptElement.parentNode?.replaceChild(clonedElement, scriptElement);
  });
}

export async function generatePreviewURL(element: HTMLElement, filter?: (node: Node) => boolean) {
  return await domtoimage.toPng(element, {
    filter: (node: Node) => {
      return domToImageDefaultFilters(node) && (!filter || filter(node));
    },
    width: element.scrollWidth,
    height: element.scrollHeight,
  });
}

export function domToImageDefaultFilters(node: Node) {
  if (!node) return true;
  if (node.nodeName.toLowerCase() === 'script') return false;
  if (node.nodeType === 1 && isElementVisible(node as HTMLElement) === false) return false;
  if (node.nodeType === 1 && (node as HTMLElement).style.visibility === 'hidden') return false;
  // filtering out svg images, because dom-to-image library does not support it.
  if (
    node.nodeName.toLowerCase() === 'img' &&
    (!(node as HTMLImageElement).src || (node as HTMLImageElement).src.indexOf('SvgImage.ashx') > -1)
  )
    return false;
  return true;
}

export function scrollToElement(element?: HTMLElement | null, scrollParent?: HTMLElement) {
  if (!element) {
    return;
  }
  const elemHeight = element.offsetTop;
  const parentHeight = scrollParent ? scrollParent.offsetHeight : window.innerHeight;
  if (elemHeight > parentHeight * 0.75) {
    element.scrollIntoView({ block: 'start', behavior: 'smooth' });
  }
}

/**
 * Fires callback when element is attached to the DOM, or immediately if it's already attached.
 *
 * **Only use where React lifecycle hooks are not in play.**
 *
 * @param el element to observe
 * @param onAttachCallback callback for when element is attached to the DOM
 * @param observer optionally reuse the observer from the first time you called this within a particular context.
 * @returns observer for reuse, in case attach / detach will happen multiple times for your scenario.
 */
export function onAttach(
  el: HTMLElement,
  onAttachCallback: () => any,
  observer = new MutationObserver(() => {
    if (!isDetached(el)) {
      observer.disconnect();
      onAttachCallback();
    }
  })
) {
  if (!isDetached(el)) {
    observer.disconnect();
    onAttachCallback();
    return observer;
  }

  observer.observe(document, {
    childList: true,
    subtree: true,
  });

  return observer;
}

/**
 * Fires callback if element is detached from the DOM, or immediately if it's already detached.
 *
 * **Only use where React lifecycle hooks are not in play.**
 *
 * @param el element to observe
 * @param onDetachCallback callback for when element is removed from the DOM
 * @param observer optionally reuse the observer from the first time you called this within a particular context.
 * @returns observer for reuse, in case attach / detach will happen multiple times for your scenario.
 */
export function onDetach(
  el: HTMLElement,
  onDetachCallback: () => any,
  observer = new MutationObserver(() => {
    if (isDetached(el)) {
      observer.disconnect();
      onDetachCallback();
    }
  })
) {
  if (isDetached(el)) {
    observer.disconnect();
    onDetachCallback();
    return observer;
  }

  observer.observe(document, {
    childList: true,
    subtree: true,
  });

  return observer;
}

export function parseTransform(el: HTMLElement): IPoint {
  // Explanation why we need m41 and m42
  // https://stackoverflow.com/questions/5968227/get-the-value-of-webkit-transform-of-an-element-with-jquery/5968313#5968313
  const transform = getComputedStyle(el).transform;
  const matrix = new DOMMatrix(transform);

  return { x: matrix.m41, y: matrix.m42 };
}
